import dayjs from 'dayjs';
import { Copy, Pencil, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError } from '@/api/client';
import {
  useApiKeys,
  useCreateApiKey,
  useRevokeApiKey,
  useUpdateApiKey,
} from '@/api/queries';
import type { ApiKey, ApiKeyCreated, RateLimitTier } from '@/api/types';
import { Empty, ErrorBox } from '@/components/empty';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const TIERS: RateLimitTier[] = ['free', 'standard', 'premium', 'enterprise'];

// Lightweight CIDR-ish syntax check; the backend is the source of truth
// (Pydantic IPvAnyNetwork). We just want to keep typos out of the form.
function parseCidrLines(text: string): { ok: string[]; bad: string[] } {
  const ok: string[] = [];
  const bad: string[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const trimmed = raw.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;
    if (/^[0-9a-fA-F:.]+(\/\d{1,3})?$/.test(trimmed)) ok.push(trimmed);
    else bad.push(trimmed);
  }
  return { ok, bad };
}

export function ApiKeysPanel({ merchantId }: { merchantId: string }) {
  const { t } = useTranslation();
  const keys = useApiKeys(merchantId);
  const create = useCreateApiKey(merchantId);
  const revoke = useRevokeApiKey(merchantId);
  const [createOpen, setCreateOpen] = useState(false);
  const [secretJustCreated, setSecretJustCreated] = useState<ApiKeyCreated | null>(null);
  const [editing, setEditing] = useState<ApiKey | null>(null);

  const onRevoke = (id: string) => {
    if (!confirm(t('api_keys.revoke_confirm'))) return;
    revoke.mutate(id, {
      onSuccess: () => toast.success(t('api_keys.revoked_toast')),
      onError: (e) => toast.error(e instanceof ApiError ? e.message : t('api_keys.revoke_failed')),
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-base font-semibold">{t('api_keys.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('api_keys.description')}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" /> {t('api_keys.new')}
        </Button>
      </div>

      {keys.isError && <ErrorBox message={t('common.error')} />}

      <div className="rounded-md border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('api_keys.cols.key_id')}</TableHead>
              <TableHead>{t('api_keys.cols.scopes')}</TableHead>
              <TableHead>{t('api_keys.cols.tier')}</TableHead>
              <TableHead>{t('api_keys.cols.allowlist')}</TableHead>
              <TableHead>{t('api_keys.cols.status')}</TableHead>
              <TableHead>{t('api_keys.cols.last_used')}</TableHead>
              <TableHead className="w-32" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {keys.data?.map((k) => (
              <TableRow key={k.id}>
                <TableCell className="font-mono text-xs">{k.key_id}</TableCell>
                <TableCell className="text-xs">
                  {k.scopes.map((s) => (
                    <Badge key={s} variant="secondary" className="mr-1">
                      {s}
                    </Badge>
                  ))}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="capitalize">
                    {k.rate_limit_tier}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {k.allowed_ips?.length ? k.allowed_ips.join(', ') : t('api_keys.any_ip')}
                </TableCell>
                <TableCell>
                  <Badge variant={k.status === 'active' ? 'success' : 'outline'}>{k.status}</Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {k.last_used_at ? dayjs(k.last_used_at).format('YYYY-MM-DD HH:mm') : '—'}
                </TableCell>
                <TableCell className="space-x-1 text-right">
                  {k.status === 'active' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditing(k)}
                        aria-label="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onRevoke(k.id)}
                        disabled={revoke.isPending}
                      >
                        {t('common.revoke')}
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!keys.isLoading && keys.data?.length === 0 && (
          <Empty title={t('api_keys.none')} className="border-0" />
        )}
      </div>

      <CreateKeyDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        pending={create.isPending}
        onSubmit={(body) =>
          create.mutate(body, {
            onSuccess: (k) => {
              setCreateOpen(false);
              setSecretJustCreated(k);
            },
            onError: (e) =>
              toast.error(e instanceof ApiError ? e.message : t('api_keys.create_failed')),
          })
        }
      />

      <EditKeyDialog
        merchantId={merchantId}
        keyData={editing}
        onClose={() => setEditing(null)}
      />

      <SecretReveal keyData={secretJustCreated} onClose={() => setSecretJustCreated(null)} />
    </div>
  );
}

function CreateKeyDialog({
  open,
  onOpenChange,
  pending,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  pending: boolean;
  onSubmit: (body: {
    scopes: string[];
    allowed_ips: string[];
    rate_limit_tier: RateLimitTier;
  }) => void;
}) {
  const [scopesText, setScopesText] = useState('payments:write\npayments:read');
  const [allowText, setAllowText] = useState('');
  const [tier, setTier] = useState<RateLimitTier>('standard');

  const { t } = useTranslation();

  const submit = () => {
    const scopes = scopesText
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
    const { ok, bad } = parseCidrLines(allowText);
    if (bad.length) {
      toast.error(t('api_keys.invalid_cidr', { entries: bad.join(', ') }));
      return;
    }
    onSubmit({ scopes, allowed_ips: ok, rate_limit_tier: tier });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('api_keys.create_title')}</DialogTitle>
          <DialogDescription>{t('api_keys.create_description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="scopes">{t('api_keys.scopes_label')}</Label>
            <textarea
              id="scopes"
              className="mt-1 w-full rounded-md border border-input bg-background p-2 font-mono text-xs"
              rows={3}
              value={scopesText}
              onChange={(e) => setScopesText(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="allow">{t('api_keys.allowlist_label')}</Label>
            <textarea
              id="allow"
              className="mt-1 w-full rounded-md border border-input bg-background p-2 font-mono text-xs"
              rows={3}
              placeholder="10.0.0.0/8&#10;192.0.2.5"
              value={allowText}
              onChange={(e) => setAllowText(e.target.value)}
            />
          </div>
          <div>
            <Label>{t('api_keys.tier_label')}</Label>
            <Select value={tier} onValueChange={(v) => setTier(v as RateLimitTier)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIERS.map((tv) => (
                  <SelectItem key={tv} value={tv} className="capitalize">
                    {t(`api_keys.tiers.${tv}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={pending}>
            {t('common.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditKeyDialog({
  merchantId,
  keyData,
  onClose,
}: {
  merchantId: string;
  keyData: ApiKey | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateApiKey(merchantId);
  const [allowText, setAllowText] = useState('');
  const [tier, setTier] = useState<RateLimitTier>('standard');

  useEffect(() => {
    if (keyData) {
      setTier(keyData.rate_limit_tier);
      setAllowText((keyData.allowed_ips || []).join('\n'));
    }
  }, [keyData]);

  const submit = () => {
    if (!keyData) return;
    const { ok, bad } = parseCidrLines(allowText);
    if (bad.length) {
      toast.error(t('api_keys.invalid_cidr', { entries: bad.join(', ') }));
      return;
    }
    update.mutate(
      { keyId: keyData.id, body: { allowed_ips: ok, rate_limit_tier: tier } },
      {
        onSuccess: () => {
          toast.success(t('api_keys.updated_toast'));
          onClose();
          setAllowText('');
        },
        onError: (e) =>
          toast.error(e instanceof ApiError ? e.message : t('api_keys.update_failed')),
      },
    );
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
      setAllowText('');
    }
  };

  return (
    <Dialog open={!!keyData} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('api_keys.edit_title')}</DialogTitle>
          <DialogDescription>{t('api_keys.edit_description')}</DialogDescription>
        </DialogHeader>
        {keyData && (
          <div className="space-y-3">
            <div className="font-mono text-xs text-muted-foreground">{keyData.key_id}</div>
            <div>
              <Label htmlFor="edit-allow">{t('api_keys.allowlist_label')}</Label>
              <textarea
                id="edit-allow"
                className="mt-1 w-full rounded-md border border-input bg-background p-2 font-mono text-xs"
                rows={3}
                value={allowText}
                onChange={(e) => setAllowText(e.target.value)}
              />
            </div>
            <div>
              <Label>{t('api_keys.tier_label')}</Label>
              <Select value={tier} onValueChange={(v) => setTier(v as RateLimitTier)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIERS.map((tv) => (
                    <SelectItem key={tv} value={tv} className="capitalize">
                      {t(`api_keys.tiers.${tv}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={update.isPending}>
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SecretReveal({
  keyData,
  onClose,
}: {
  keyData: ApiKeyCreated | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={!!keyData} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('api_keys.secret_revealed_title')}</DialogTitle>
          <DialogDescription>{t('api_keys.secret_revealed_description')}</DialogDescription>
        </DialogHeader>
        {keyData && (
          <div className="space-y-3">
            <div className="rounded-md border border-border bg-muted/30 p-3">
              <code className="text-xs break-all">{keyData.secret}</code>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(keyData.secret);
                toast.success(t('common.copied'));
              }}
            >
              <Copy className="h-4 w-4" /> {t('common.copy')}
            </Button>
          </div>
        )}
        <DialogFooter>
          <Button onClick={onClose}>{t('api_keys.secret_revealed_ack')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
